/**
 * AquaStar User SPA — Nexus collab module API client.
 *
 * Covers the collab endpoints that end-users (non-admins) call:
 *   - Meeting invites (notification bell)
 *   - Join an active session (get a personal Jitsi JWT)
 *   - Check if a session is active for a room
 *
 * All calls go directly to the Nexus gateway at `GATEWAY_BASE/v1/collab/...`
 * with the user's Bearer token.
 */

import { GATEWAY_BASE } from './config';
import { ApiError, getToken } from './apiClient';

function bearerHeader(): Record<string, string> {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function collabFetch<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = { ...bearerHeader() };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const res = await fetch(`${GATEWAY_BASE}/v1/collab${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) {
    // Token expired — let the caller handle
    throw new ApiError(401, 'Unauthorized');
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let msg = `HTTP ${res.status}`;
    try {
      const json = JSON.parse(text) as { message?: string; code?: string };
      if (json.message) msg = json.message;
    } catch {
      if (text) msg = text.slice(0, 200);
    }
    throw new ApiError(res.status, msg);
  }

  const text = await res.text();
  if (!text) return undefined as unknown as T;
  return JSON.parse(text) as T;
}

// ── Types ──────────────────────────────────────────────────────────────────

export interface CollabInvite {
  id: string;
  roomId: string;
  /**
   * null = "you were added to room X" (proactive at add time, no live session).
   * string = "session Y started in room X" (meeting is/was live).
   */
  sessionId: string | null;
  userId: string;
  roomName: string;
  startedByName: string | null;
  createdAt: string;
  readAt: string | null;
}

export interface JitsiJoinToken {
  jwt: string;
  roomUrl: string;
  jitsiUrl: string;
  sessionId: string;
  jitsiRoomName: string;
  iceServers: Array<{ urls: string[]; username?: string; credential?: string }>;
}

export interface ActiveSession {
  id: string;
  roomId: string;
  jitsiRoomName: string;
  startedBy: string;
  startedAt: string;
  endedAt: string | null;
}

// ── API functions ──────────────────────────────────────────────────────────

/**
 * List meeting invites for the authenticated user.
 * Pass `unreadOnly: true` to get only unseen invites (for badge count).
 */
export async function getInvites(opts: { unreadOnly?: boolean; limit?: number } = {}): Promise<CollabInvite[]> {
  const params = new URLSearchParams();
  if (opts.unreadOnly) params.set('unread', 'true');
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString() ? `?${params}` : '';
  const data = await collabFetch<{ invites: CollabInvite[] }>('GET', `/invites${qs}`);
  return data.invites;
}

/**
 * Mark a meeting invite as read.
 */
export async function markInviteRead(inviteId: string): Promise<void> {
  await collabFetch<{ read: boolean }>('PATCH', `/invites/${inviteId}/read`);
}

/**
 * Delete (dismiss) a meeting invite. Used by the × button on the bell
 * popover and the notifications page to clean up stale entries.
 */
export async function deleteInvite(inviteId: string): Promise<void> {
  await collabFetch<{ deleted: boolean }>('DELETE', `/invites/${inviteId}`);
}

/**
 * Join an existing active session for a room.
 * Returns a personal Jitsi JWT + the jitsiUrl to open.
 * Throws ApiError(409) if no active session is in progress.
 * Throws ApiError(403) if the user is not a room member.
 */
export async function joinSession(
  roomId: string,
  displayName?: string,
): Promise<JitsiJoinToken> {
  return collabFetch<JitsiJoinToken>('POST', `/rooms/${roomId}/sessions/join`, {
    display_name: displayName,
  });
}

/**
 * Get the current active session for a room.
 * Returns null if no session is in progress (404 from server).
 */
export async function getActiveSession(roomId: string): Promise<ActiveSession | null> {
  try {
    const data = await collabFetch<{ session: ActiveSession }>('GET', `/rooms/${roomId}/session/active`);
    return data.session;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

/**
 * Build a room SSE URL for subscribing to room-level events
 * (session:started, session:ended, meeting_invite, message:new).
 * Requires Bearer auth via EventSourcePolyfill.
 */
export function roomStreamUrl(roomId: string): string {
  return `${GATEWAY_BASE}/v1/collab/rooms/${roomId}/stream`;
}
